import { DateTime } from "luxon";

import { getFullName } from "utils";

import type { CarrierMemberStats } from "types/company/carrier";

export type SortKey =
  "name" | "memberSince" | "quotes" | "bookings" | "satisfaction";
export type SortOrder = "asc" | "desc";

export interface SortState {
  sortKey: SortKey;
  order: SortOrder;
}

export function initialsOf(member: CarrierMemberStats): string {
  return [member.first_name, member.last_name]
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
}

function sortValue(member: CarrierMemberStats, key: SortKey): number | string {
  if (key === "name") return getFullName(member.first_name, member.last_name);
  if (key === "memberSince")
    return DateTime.fromISO(member.member_since).toMillis();
  if (key === "quotes") return member.quotes_created;
  if (key === "bookings") return member.loads_booked;
  // `sortMembers` holds unrated members out of the comparison, so this fallback
  // is unreachable and only keeps the return type a number.
  return member.micro_review_rating.rating ?? 0;
}

function compareMembers(
  first: CarrierMemberStats,
  second: CarrierMemberStats,
  sortKey: SortKey,
): number {
  const firstValue = sortValue(first, sortKey);
  const secondValue = sortValue(second, sortKey);

  return typeof firstValue === "string"
    ? firstValue.localeCompare(secondValue as string)
    : firstValue - (secondValue as number);
}

export interface SortMembersProps {
  members: CarrierMemberStats[];
  sortKey: SortKey;
  order: SortOrder;
}

/*
Orders the team table. An unrated member has no satisfaction to compare, so
rather than standing in a number for the missing rating they are held out of
the comparison and appended, keeping them last in both directions: "no rating"
is not a low rating, which is the same reason the payload reports `rating` as
null rather than 0.
*/
export function sortMembers(props: SortMembersProps): CarrierMemberStats[] {
  const { members, sortKey, order } = props;
  const direction = order === "asc" ? 1 : -1;

  const isUnrated = (member: CarrierMemberStats) =>
    sortKey === "satisfaction" && member.micro_review_rating.rating == null;

  const rated = members.filter((member) => !isUnrated(member));
  const unrated = members.filter(isUnrated);

  const sorted = [...rated].sort(
    (first, second) => direction * compareMembers(first, second, sortKey),
  );

  return [...sorted, ...unrated];
}

export interface NextSortStateProps {
  current: SortState;
  clickedKey: SortKey;
}

/*
Clicking the active column flips its direction. Moving to another column starts
it at the direction that puts the interesting rows on top: names read from A,
every other column leads with the highest figure.
*/
export function nextSortState(props: NextSortStateProps): SortState {
  const { current, clickedKey } = props;

  if (clickedKey === current.sortKey) {
    return {
      sortKey: current.sortKey,
      order: current.order === "asc" ? "desc" : "asc",
    };
  }

  return {
    sortKey: clickedKey,
    order: clickedKey === "name" ? "asc" : "desc",
  };
}

export function formatMemberSince(memberSince: string): string {
  return DateTime.fromISO(memberSince).toFormat("MMM yyyy");
}

export function formatQuotesTooltip(count: number): string {
  const noun = count === 1 ? "quote" : "quotes";

  return `${count.toLocaleString()} ${noun} submitted on Evotrux.`;
}

export function formatBookingsTooltip(count: number): string {
  const noun = count === 1 ? "load" : "loads";

  return `${count.toLocaleString()} ${noun} booked on Evotrux.`;
}

export function formatRatedLoadsTooltip(count: number): string {
  const clause = count === 1 ? "load has" : "loads have";

  return `${count.toLocaleString()} ${clause} been rated on Evotrux after delivery.`;
}
